import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import {
  DealerUsageSort,
  IChatDealerBreakdown,
  IChatDealerUsageReport,
  IChatDealerUsageRow,
  IChatUserUsageReport,
  IDealerUsageParams,
  UserUsageSort,
} from '../models'
import { DEFAULT_EXCLUDE_TEST_DEALERS, DealerTypeFilter } from '../analyticsQuery'
import { DEFAULT_LOAD_ERROR_TEXT, formatCount, formatDuration, formatPercent, loadErrorMessage } from '../viewModel'
import DealerUsageCharts from './DealerUsageCharts'
import DealerBreakdownPanel from './DealerBreakdownPanel'
import { buildDealerUsageSheetRows, buildUserUsageSheetRows, exportUsageToExcel } from '../dealerUsageExcel'
import styles from './ChatbotAnalytics.module.scss'
import { RemoteBadge } from './RemoteBadge'

const PAGE_SIZE = 50
const EXPORT_PAGE_SIZE = 5000
const SEARCH_DEBOUNCE_MS = 400
const DASH = '—'

/** Oyların zaman ekseni Genel Bakış'tan farklıdır; kullanıcı bunu başlıkta görsün (ADR-017). */
const VOTE_AXIS_HINT =
  'Oylar tur tarihine göre süzülür; Genel Bakış sekmesi oy tarihine göre süzer. İki ekrandaki sayılar birebir tutmayabilir.'

type UsageView = 'dealer' | 'user'
type LoadState = 'loading' | 'ready' | 'empty' | 'error'

export interface DealerUsageTabProps {
  fromUtc: Date
  toUtc: Date
  loadDealerUsage: (params: IDealerUsageParams) => Promise<IChatDealerUsageReport>
  loadUserUsage: (params: IDealerUsageParams) => Promise<IChatUserUsageReport>
  loadBreakdown: (dealerSapCode: string, fromUtc: Date, toUtc: Date) => Promise<IChatDealerBreakdown>
  /** "Deneme Kartı Hariç" (shell'den gelir; Excel dahil her isteğe eklenir). */
  excludeTestDealers?: boolean
  /** "Bayi Tipi" (shell'den gelir; Excel dahil her isteğe eklenir). */
  dealerType?: DealerTypeFilter
}

const dealerColumns: { key: DealerUsageSort; label: string }[] = [
  { key: 'dealer', label: 'Bayi' },
  { key: 'turns', label: 'Tur' },
  { key: 'users', label: 'Tekil Kullanıcı' },
  { key: 'successRate', label: 'Teknik Başarı' },
  { key: 'errors', label: 'Hata' },
  { key: 'duration', label: 'Ort. Süre' },
  { key: 'lastUsed', label: 'Son Kullanım' },
]

const userColumns: { key: UserUsageSort; label: string }[] = [
  { key: 'user', label: 'Kullanıcı' },
  { key: 'dealer', label: 'Bayi' },
  { key: 'turns', label: 'Tur' },
  { key: 'successRate', label: 'Teknik Başarı' },
  { key: 'lastUsed', label: 'Son Kullanım' },
]

const utcDate = (iso: string): string => new Date(iso).toLocaleDateString('tr-TR', { timeZone: 'UTC' })

export default function DealerUsageTab({
  fromUtc,
  toUtc,
  loadDealerUsage,
  loadUserUsage,
  loadBreakdown,
  excludeTestDealers = DEFAULT_EXCLUDE_TEST_DEALERS,
  dealerType,
}: DealerUsageTabProps) {
  const [view, setView] = useState<UsageView>('dealer')
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  const [dealerFilter, setDealerFilter] = useState<string | undefined>(undefined)
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState<DealerUsageSort | UserUsageSort>('turns')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [loadState, setLoadState] = useState<LoadState>('loading')
  // Sunucunun kullanıcıya dönük hata metni (ör. 90 gün sınırı) panelde gösterilir.
  const [errorMessage, setErrorMessage] = useState(DEFAULT_LOAD_ERROR_TEXT)
  const [dealerReport, setDealerReport] = useState<IChatDealerUsageReport | null>(null)
  const [userReport, setUserReport] = useState<IChatUserUsageReport | null>(null)
  const [expandedDealer, setExpandedDealer] = useState<string | null>(null)
  const [breakdownCache, setBreakdownCache] = useState<Record<string, IChatDealerBreakdown>>({})
  const [exporting, setExporting] = useState(false)

  const requestSequenceRef = useRef(0)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      requestSequenceRef.current += 1
    }
  }, [])

  // Arama kutusu her tuşta istek atmaz; terim değişince sayfa 1'e döner.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(current => (current === searchDraft ? current : searchDraft))
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchDraft])

  const currentParams = useCallback(
    (overrides?: Partial<IDealerUsageParams>): IDealerUsageParams => ({
      fromUtc,
      toUtc,
      search: search || undefined,
      dealerSapCode: view === 'user' ? dealerFilter : undefined,
      sortBy,
      sortDir,
      page,
      pageSize: PAGE_SIZE,
      export: false,
      excludeTestDealers,
      dealerType,
      ...overrides,
    }),
    [fromUtc, toUtc, search, view, dealerFilter, sortBy, sortDir, page, excludeTestDealers, dealerType]
  )

  const load = useCallback(async () => {
    const sequence = ++requestSequenceRef.current
    setLoadState('loading')

    try {
      if (view === 'dealer') {
        const report = await loadDealerUsage(currentParams())
        // Geç dönen yanıt yeniyi EZMEZ (index.tsx'teki requestSequence deseninin aynısı).
        if (!mountedRef.current || sequence !== requestSequenceRef.current) return
        setDealerReport(report)
        setLoadState(report.Page.TotalRows === 0 ? 'empty' : 'ready')
      } else {
        const report = await loadUserUsage(currentParams())
        if (!mountedRef.current || sequence !== requestSequenceRef.current) return
        setUserReport(report)
        setLoadState(report.Page.TotalRows === 0 ? 'empty' : 'ready')
      }
    } catch (error) {
      if (!mountedRef.current || sequence !== requestSequenceRef.current) return
      setErrorMessage(loadErrorMessage(error))
      setLoadState('error')
    }
  }, [view, currentParams, loadDealerUsage, loadUserUsage])

  useEffect(() => {
    load()
  }, [load])

  const toggleSort = (key: DealerUsageSort | UserUsageSort) => {
    if (key === sortBy) {
      setSortDir(current => (current === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortBy(key)
      setSortDir('desc')
    }
    setPage(1)
  }

  const switchView = (next: UsageView) => {
    if (next === view) return
    setView(next)
    setSortBy('turns')
    setSortDir('desc')
    setPage(1)
    setExpandedDealer(null)
    if (next === 'dealer') setDealerFilter(undefined)
  }

  const showUsersOfDealer = (dealerSapCode: string) => {
    setView('user')
    setDealerFilter(dealerSapCode)
    setSortBy('turns')
    setSortDir('desc')
    setPage(1)
    setExpandedDealer(null)
  }

  const cacheBreakdown = useCallback((result: IChatDealerBreakdown) => {
    setBreakdownCache(current => ({ ...current, [result.DealerSapCode]: result }))
  }, [])

  const exportToExcel = async () => {
    setExporting(true)
    try {
      // Sayfalanmış görünüm değil, aynı filtrelerin TAM sonuç kümesi indirilir.
      const params = currentParams({ page: 1, pageSize: EXPORT_PAGE_SIZE, export: true })
      if (view === 'dealer') {
        const report = await loadDealerUsage(params)
        exportUsageToExcel(buildDealerUsageSheetRows(report.Page.Rows), 'chatbot-bayi-kullanim.xlsx')
      } else {
        const report = await loadUserUsage(params)
        exportUsageToExcel(buildUserUsageSheetRows(report.Page.Rows), 'chatbot-kullanici-kullanim.xlsx')
      }
    } catch (error) {
      if (mountedRef.current) {
        setErrorMessage(loadErrorMessage(error))
        setLoadState('error')
      }
    } finally {
      if (mountedRef.current) setExporting(false)
    }
  }

  const voteCell = (row: IChatDealerUsageRow): string =>
    // Eşiğin altında sunucu null gönderir (ADR-017); istemci sayı UYDURMAZ.
    row.PositiveVotes === null || row.NegativeVotes === null
      ? DASH
      : `${formatCount(row.PositiveVotes)} / ${formatCount(row.NegativeVotes)}`

  const sortMark = (key: DealerUsageSort | UserUsageSort): string =>
    sortBy === key ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''

  return (
    <section className={styles.page} data-testid='usage-tab-panel'>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Site Raporları</span>
          <h1>Bayi &amp; Kullanıcı Kullanımı</h1>
          <p>Seçilen aralıkta chatbot kullanan bayileri ve kullanıcıları karşılaştırın.</p>
        </div>
        <span className={styles.privacyBadge}>Konuşma içeriği gösterilmez</span>
      </header>

      <div className={styles.usageToolbar}>
        <button
          type='button'
          data-testid='usage-view-dealer'
          className={view === 'dealer' ? styles.primaryButton : styles.secondaryButton}
          onClick={() => switchView('dealer')}
        >
          Bayi
        </button>
        <button
          type='button'
          data-testid='usage-view-user'
          className={view === 'user' ? styles.primaryButton : styles.secondaryButton}
          onClick={() => switchView('user')}
        >
          Kullanıcı
        </button>
        <input
          type='text'
          data-testid='usage-search'
          className={styles.dateInput}
          value={searchDraft}
          aria-label='Kullanım araması'
          placeholder={view === 'dealer' ? 'Bayi ünvanı veya SAP kodu' : 'Kullanıcı, bayi veya SAP kodu'}
          onChange={event => setSearchDraft(event.target.value)}
        />
        <button
          type='button'
          data-testid='usage-export'
          className={styles.secondaryButton}
          disabled={exporting}
          onClick={exportToExcel}
        >
          Excel&apos;e Aktar
        </button>
        {view === 'user' && dealerFilter ? (
          <button
            type='button'
            data-testid='usage-clear-dealer-filter'
            className={styles.secondaryButton}
            onClick={() => {
              setDealerFilter(undefined)
              setPage(1)
            }}
          >
            Bayi filtresini kaldır ({dealerFilter})
          </button>
        ) : null}
      </div>

      {loadState === 'error' ? (
        <section className={`${styles.statePanel} ${styles.errorState}`} role='alert' data-testid='usage-error'>
          <span className={styles.stateIcon} aria-hidden='true'>
            !
          </span>
          <h2>Kullanım verileri yüklenemedi</h2>
          <p>{errorMessage}</p>
        </section>
      ) : null}

      {loadState === 'empty' ? (
        <section className={styles.statePanel} role='status' data-testid='usage-empty'>
          <span className={styles.stateIcon} aria-hidden='true'>
            —
          </span>
          <h2>Henüz veri yok</h2>
          <p>Seçilen tarih aralığında chatbot kullanımı bulunamadı.</p>
        </section>
      ) : null}

      {loadState === 'loading' ? (
        <section className={styles.statePanel} role='status' aria-live='polite' data-testid='usage-loading'>
          <span className={styles.loadingMark} aria-hidden='true' />
          <h2>Kullanım verileri yükleniyor</h2>
        </section>
      ) : null}

      {view === 'dealer' && dealerReport && loadState === 'ready' ? (
        <>
          <div className={styles.kpiGrid}>
            <article className={styles.kpiCard}>
              <span>Kullanan Bayi</span>
              <strong>{formatCount(dealerReport.Totals.DealerCount)}</strong>
            </article>
            <article className={styles.kpiCard}>
              <span>Kullanan Kişi</span>
              <strong>{formatCount(dealerReport.Totals.UserCount)}</strong>
            </article>
            <article className={styles.kpiCard}>
              <span>Toplam Tur</span>
              <strong>{formatCount(dealerReport.Totals.TurnCount)}</strong>
            </article>
            <article className={styles.kpiCard}>
              <span>Bayi Başına Tur</span>
              <strong>{formatCount(dealerReport.Totals.AverageTurnsPerDealer)}</strong>
            </article>
          </div>

          {dealerReport.DailyActiveDealers.length > 0 || dealerReport.TopDealersByTurns.length > 0 ? (
            <DealerUsageCharts data={dealerReport} />
          ) : null}

          <div className={styles.tableScroll}>
            <table>
              <thead>
                <tr>
                  {dealerColumns.map(column => (
                    <th
                      key={column.key}
                      scope='col'
                      className={styles.sortableHeader}
                      data-testid={`usage-header-${column.key}`}
                      onClick={() => toggleSort(column.key)}
                    >
                      {column.label}
                      {sortMark(column.key)}
                    </th>
                  ))}
                  <th scope='col' title={VOTE_AXIS_HINT}>
                    Oy (+/−)
                  </th>
                  <th scope='col'>Detay</th>
                </tr>
              </thead>
              <tbody>
                {dealerReport.Page.Rows.map(row => (
                  <Fragment key={row.DealerSapCode}>
                    <tr data-testid={`usage-row-${row.DealerSapCode}`}>
                      <th scope='row'>
                        {row.DealerName ?? DASH}
                        <small> {row.DealerSapCode}</small>
                        <RemoteBadge count={row.RemoteTurnCount ?? 0} testId={`usage-remote-${row.DealerSapCode}`} />
                      </th>
                      <td>{formatCount(row.TurnCount)}</td>
                      <td>{formatCount(row.UserCount)}</td>
                      <td>{formatPercent(row.TechnicalSuccessRate)}</td>
                      <td>{formatCount(row.ErrorCount)}</td>
                      <td>{formatDuration(row.AverageDurationMs)}</td>
                      <td>{utcDate(row.LastUsedUtc)}</td>
                      <td data-testid={`usage-votes-${row.DealerSapCode}`}>{voteCell(row)}</td>
                      <td>
                        <button
                          type='button'
                          data-testid={`breakdown-toggle-${row.DealerSapCode}`}
                          className={styles.secondaryButton}
                          onClick={() =>
                            setExpandedDealer(current =>
                              current === row.DealerSapCode ? null : row.DealerSapCode
                            )
                          }
                        >
                          Detay
                        </button>
                      </td>
                    </tr>
                    {expandedDealer === row.DealerSapCode ? (
                      <tr>
                        <td colSpan={dealerColumns.length + 2}>
                          <DealerBreakdownPanel
                            dealerSapCode={row.DealerSapCode}
                            fromUtc={fromUtc}
                            toUtc={toUtc}
                            cached={breakdownCache[row.DealerSapCode]}
                            loadBreakdown={loadBreakdown}
                            onLoaded={cacheBreakdown}
                            onShowUsers={showUsersOfDealer}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <UsagePagination
            page={page}
            pageSize={PAGE_SIZE}
            totalRows={dealerReport.Page.TotalRows}
            onPageChange={setPage}
          />
        </>
      ) : null}

      {view === 'user' && userReport && loadState === 'ready' ? (
        <>
          <div className={styles.tableScroll}>
            <table>
              <thead>
                <tr>
                  {userColumns.map(column => (
                    <th
                      key={column.key}
                      scope='col'
                      className={styles.sortableHeader}
                      data-testid={`usage-header-${column.key}`}
                      onClick={() => toggleSort(column.key)}
                    >
                      {column.label}
                      {sortMark(column.key)}
                    </th>
                  ))}
                  {/* OY SÜTUNU YOKTUR — kişi bazında oy görünürlüğü kapsam dışıdır (ADR-017). */}
                </tr>
              </thead>
              <tbody>
                {userReport.Page.Rows.map(row => (
                  <tr key={`${row.DealerSapCode}-${row.UserId}`} data-testid={`usage-user-row-${row.UserId}`}>
                    <th scope='row'>
                      {row.FullName ?? DASH}
                      <small> {row.UserName ?? DASH}</small>
                    </th>
                    <td>
                      {row.DealerName ?? DASH}
                      {row.IsRemote ? <RemoteBadge testId={`usage-user-remote-${row.UserId}`} /> : null}
                    </td>
                    <td>{formatCount(row.TurnCount)}</td>
                    <td>{formatPercent(row.TechnicalSuccessRate)}</td>
                    <td>{utcDate(row.LastUsedUtc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <UsagePagination
            page={page}
            pageSize={PAGE_SIZE}
            totalRows={userReport.Page.TotalRows}
            onPageChange={setPage}
          />
        </>
      ) : null}
    </section>
  )
}

interface UsagePaginationProps {
  page: number
  pageSize: number
  totalRows: number
  onPageChange: (page: number) => void
  /** Aynı sayfada birden çok sekme sayfalama kullanır; test kimlikleri sekmeye göre ayrışır. */
  testIdPrefix?: string
}

/** Sunucu tarafı sayfalama (50'lik sayfa); Geri Bildirimler ve Sorular sekmeleri de kullanır. */
export function UsagePagination({
  page,
  pageSize,
  totalRows,
  onPageChange,
  testIdPrefix = 'usage',
}: UsagePaginationProps) {
  const lastPage = Math.max(1, Math.ceil(totalRows / pageSize))

  return (
    <nav className={styles.usagePagination} aria-label='Sayfalama'>
      <button
        type='button'
        data-testid={`${testIdPrefix}-page-prev`}
        className={styles.secondaryButton}
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        Önceki
      </button>
      <span data-testid={`${testIdPrefix}-page-info`}>
        {formatCount(page)} / {formatCount(lastPage)} · {formatCount(totalRows)} kayıt
      </span>
      <button
        type='button'
        data-testid={`${testIdPrefix}-page-next`}
        className={styles.secondaryButton}
        disabled={page >= lastPage}
        onClick={() => onPageChange(page + 1)}
      >
        Sonraki
      </button>
    </nav>
  )
}
